import { Box, Button, Popover, Typography } from '@mui/material';
import { TokenResponse, useGoogleLogin } from '@react-oauth/google';
import React, { useEffect, useState } from 'react';
import AccessTimedOutIcon from '../../../../assets/icons/AccessTimedOut.svg';
import GoogleSheetsDocsIcon from '../../../../assets/icons/GoogleSheetsDocs.svg';
import TrashIcon from '../../../../assets/icons/Trash.svg';
import Connected from '../../../../assets/icons/connected.svg';
import GoogleIcon from '../../../../assets/icons/google.svg';
import MoreIcon from '../../../../assets/icons/more.svg';
import { AuthService } from '../../../../service/AuthService';
import { LocalStorageService } from '../../../../service/LocalStorageService';
import { UserService } from '../../../../service/UserService';
import { useStoreGoogleData } from '../../../../store/storeGoogleData';
import {
  NotificationType,
  useNotificationStore,
} from '../../../../store/storeNotifications';
import {
  getConnectedEmail,
  isGoogleSheetsAccessTokenValid,
} from '../../../GoogleSheetsIntegration/helpers';
import LogoutModal from '../../../Integrations/LogoutModal';
import type { ConnectionCardProps } from '../../types';

const scope =
  'https://www.googleapis.com/auth/spreadsheets https://www.googleapis.com/auth/drive https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.readonly https://www.googleapis.com/auth/spreadsheets.readonly https://www.googleapis.com/auth/documents https://www.googleapis.com/auth/documents.readonly https://www.googleapis.com/auth/presentations https://www.googleapis.com/auth/presentations.readonly https://www.googleapis.com/auth/gmail.send';

enum ConnectionState {
  NotConnected = 'Not Connected',
  JustConnected = 'Just Connected',
  Connected = 'Connected',
  AccessTimedOut = 'Access Timed Out',
}

type LoginSuccessTokenResponse = Omit<
  TokenResponse,
  'error' | 'error_description' | 'error_uri'
>;
type LoginErrorResponse = Pick<
  TokenResponse,
  'error' | 'error_description' | 'error_uri'
>;

const GoogleConnectionCard: React.FC<ConnectionCardProps> = () => {
  const setNotification = useNotificationStore(state => state.setNotification);
  const {
    setGoogleRefreshToken,
    googleRefreshToken,
    setGoogleAccessToken,
    googleAccessToken,
  } = useStoreGoogleData();

  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [connectionState, setConnectionState] = useState<ConnectionState>(
    ConnectionState.NotConnected,
  );
  const [connectedEmail, setConnectedEmail] = useState('');
  const [logoutPopoverAnchorEl, setLogoutPopoverAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const logoutPopoverOpen = Boolean(logoutPopoverAnchorEl);

  const handleOpenLogoutPopover = (event: any) => {
    setLogoutPopoverAnchorEl(event.currentTarget);
  };

  const handleCloseLogoutPopover = () => {
    setLogoutPopoverAnchorEl(null);
  };

  useEffect(() => {
    const getConnectedEmailHandler = async () => {
      if (
        connectionState === ConnectionState.Connected ||
        connectionState === ConnectionState.JustConnected
      ) {
        if (googleAccessToken) {
          try {
            const email = await getConnectedEmail(googleAccessToken);
            setConnectedEmail(email);
          } catch (error) {
            setNotification({
              type: NotificationType.Error,
              message: 'Something went wrong fetching connected email',
            });
          }
        }
      }
    };
    getConnectedEmailHandler();
  }, [connectionState]);

  const refreshAuthToken = async (refreshToken: string) => {
    try {
      const newTokens = await AuthService.googleRefreshToken({
        refresh_token: refreshToken,
      });
      setGoogleAccessToken(newTokens.data.access_token);
      setGoogleRefreshToken(newTokens.data.refresh_token);
      setConnectionState(ConnectionState.Connected);
    } catch (refreshError) {}
  };

  useEffect(() => {
    const validateExistingGoogleSheetsAccessToken = async () => {
      if (
        googleAccessToken &&
        (await isGoogleSheetsAccessTokenValid(googleAccessToken))
      ) {
        setConnectionState(ConnectionState.Connected);
      } else if (googleRefreshToken) {
        await refreshAuthToken(googleRefreshToken);
      }
    };
    validateExistingGoogleSheetsAccessToken();
  }, []);

  const logoutHandler = async () => {
    try {
      try {
        await AuthService.disconnectGoogle();
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: 'Something went wrong disconnecting Google account',
        });
      }
      LocalStorageService.removeItem('googleSheetsAccessToken');
      LocalStorageService.removeItem('googleRefreshToken');
      LocalStorageService.removeItem('accessToken');
      setConnectionState(ConnectionState.NotConnected);
      setLogoutPopoverAnchorEl(null);
      setShowLogoutModal(false);
      setGoogleAccessToken(null);
      setGoogleRefreshToken(null);
      try {
        await UserService.updateUser({
          googleAccessToken: null,
          googleRefreshToken: null,
        });
      } catch (error) {}
    } catch (error) {
    } finally {
      window.location.href = '/login';
    }
  };

  const loginSuccessHandler = (tokenResponse: LoginSuccessTokenResponse) => {
    setConnectionState(ConnectionState.JustConnected);
    setGoogleAccessToken(tokenResponse.access_token);
  };

  const loginErrorHandler = (errorResponse: LoginErrorResponse) => {
    let errorMessage = 'Something went wrong!';
    if (errorResponse.error_description) {
      errorMessage = errorResponse.error_description;
    }
    setNotification({ type: NotificationType.Error, message: errorMessage });
  };

  const connectHandler = useGoogleLogin({
    onSuccess: async codeResponse => {
      const tokens = await AuthService.getGoogleRefreshToken({
        code: codeResponse.code,
      });
      setGoogleAccessToken(tokens.data.access_token);
      setGoogleRefreshToken(tokens.data.refresh_token);
      await UserService.updateUser({
        googleAccessToken: tokens.data.access_token,
        googleRefreshToken: tokens.data.refresh_token,
      });
      const loginSuccessTokens: TokenResponse = {
        access_token: tokens.data.access_token,
        expires_in: tokens.data.expiry_date,
        prompt: 'consent',
        token_type: '',
        scope,
      };
      loginSuccessHandler(loginSuccessTokens);
    },
    onError: loginErrorHandler,
    scope,
    flow: 'auth-code',
  });

  const renderConnectionState = () => {
    if (connectionState === ConnectionState.NotConnected) {
      return (
        <Box
          sx={{
            width: 'min-content',
            minWidth: '250px',
            border: '1px solid rgba(196, 207, 255, 1)',
            padding: '24px',
            borderRadius: '8px',
            position: 'relative',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              borderBottom: '.5px solid rgba(234, 236, 240, 1)',
            }}
          >
            <GoogleIcon />
            <Typography
              sx={{
                color: 'rgba(71, 84, 103, 1)',
                fontWeight: 600,
                fontSize: '12px',
                lineHeight: '19.2px',
                letterSpacing: '0.15px',
              }}
            >
              Google Account
            </Typography>
          </Box>
          <Typography
            sx={{
              color: 'rgba(102, 112, 133, 1)',
              fontSize: '12px',
              lineHeight: '19.2px',
              letterSpacing: '0.15px',
              marginTop: '20px',
            }}
          >
            Easily sync Google Account in FluxPrompt
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GoogleSheetsDocsIcon />
            <Typography
              sx={{
                color: 'rgba(102, 112, 133, 1)',
                fontSize: '12px',
                lineHeight: '14px',
                marginTop: '15px',
              }}
            >
              You'll grant access to Google Docs and Sheets.
            </Typography>
          </Box>
          <Button
            variant='text'
            sx={{
              color: 'rgba(54, 80, 222, 1)',
              fontSize: '12px',
              lineHeight: '14px',
              fontWeight: 400,
              marginTop: '10px',
            }}
            onClick={() => connectHandler()}
          >
            Connect
          </Button>
        </Box>
      );
    }

    if (
      connectionState === ConnectionState.JustConnected ||
      connectionState === ConnectionState.Connected
    ) {
      return (
        <Box
          sx={{
            width: 'min-content',
            minWidth: '250px',
            border: '1px solid rgba(196, 207, 255, 1)',
            padding: '24px',
            borderRadius: '8px',
            position: 'relative',
          }}
        >
          <Box sx={{ borderBottom: '.5px solid rgba(234, 236, 240, 1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <GoogleIcon />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  sx={{
                    color: 'rgba(71, 84, 103, 1)',
                    fontWeight: 600,
                    fontSize: '12px',
                    lineHeight: '19.2px',
                    letterSpacing: '0.15px',
                  }}
                >
                  Google Account
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    lineHeight: '19.2px',
                    letterSpacing: '0.15px',
                    color: 'rgba(102, 112, 133, 1)',
                  }}
                >
                  {connectedEmail}
                </Typography>
              </Box>
              <Box
                sx={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={handleOpenLogoutPopover}
              >
                <MoreIcon />
              </Box>
            </Box>
            {connectionState === ConnectionState.JustConnected && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Connected />
                <Typography
                  sx={{
                    color: 'rgba(102, 112, 133, 1)',
                    fontSize: '12px',
                    lineHeight: '19.2px',
                    letterSpacing: '0.15px',
                    marginTop: '20px',
                  }}
                >
                  You are successfully logged in to your Google account.
                </Typography>
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GoogleSheetsDocsIcon />
            <Typography
              sx={{
                color: 'rgba(102, 112, 133, 1)',
                fontSize: '12px',
                lineHeight: '14px',
                marginTop: '15px',
              }}
            >
              {connectionState === ConnectionState.JustConnected
                ? "You'll grant access to Google Docs and Sheets."
                : 'You are currently connected to Google Docs and Sheets.'}
            </Typography>
          </Box>
        </Box>
      );
    }

    if (connectionState === ConnectionState.AccessTimedOut) {
      return (
        <Box
          sx={{
            width: 'min-content',
            minWidth: '250px',
            border: '1px solid rgba(196, 207, 255, 1)',
            padding: '24px',
            borderRadius: '8px',
            position: 'relative',
          }}
        >
          <Box sx={{ borderBottom: '.5px solid rgba(234, 236, 240, 1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <GoogleIcon />
              <Typography
                sx={{
                  color: 'rgba(71, 84, 103, 1)',
                  fontWeight: 600,
                  fontSize: '12px',
                  lineHeight: '19.2px',
                  letterSpacing: '0.15px',
                }}
              >
                Google Account
              </Typography>
              <Box
                sx={{ marginLeft: 'auto', cursor: 'pointer' }}
                onClick={handleOpenLogoutPopover}
              >
                <MoreIcon />
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <AccessTimedOutIcon />
              <Typography
                sx={{
                  color: 'rgba(102, 112, 133, 1)',
                  fontSize: '12px',
                  lineHeight: '19.2px',
                  letterSpacing: '0.15px',
                  marginTop: '20px',
                }}
              >
                Access timed out. Reconnect to continue using Google connections
                in your workflows.
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <GoogleSheetsDocsIcon />
            <Typography
              sx={{
                color: 'rgba(102, 112, 133, 1)',
                fontSize: '12px',
                lineHeight: '14px',
                marginTop: '15px',
              }}
            >
              You'll grant access to Google Docs and Sheets.
            </Typography>
          </Box>
          <Button
            variant='text'
            sx={{
              color: 'rgba(54, 80, 222, 1)',
              fontSize: '12px',
              lineHeight: '14px',
              fontWeight: 400,
              marginTop: '10px',
            }}
            onClick={() => connectHandler()}
          >
            Connect
          </Button>
        </Box>
      );
    }
    return null;
  };

  return (
    <>
      {showLogoutModal && (
        <LogoutModal
          onHide={() => setShowLogoutModal(false)}
          logoutHandler={logoutHandler}
        />
      )}
      {renderConnectionState()}
      <Popover
        open={logoutPopoverOpen}
        anchorEl={logoutPopoverAnchorEl}
        onClose={handleCloseLogoutPopover}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px',
            borderRadius: '8px',
            cursor: 'pointer',
          }}
          onClick={() => setShowLogoutModal(true)}
        >
          <TrashIcon />
          <Typography>Disconnect</Typography>
        </Box>
      </Popover>
    </>
  );
};

export default GoogleConnectionCard;
